import { useState, useCallback, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { fetchSessions, createSessionAPI, deleteSessionAPI, fetchSessionById } from '../services/api';
import { Message, Session } from '../types';
import { mapSessionRecordsToMessages } from '../utils/sessionMapper';

export default function useSessions(email: string | null) {
  const [sessions, setSessions] = useState<Record<string, Session>>({});
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [sessionsLoaded, setSessionsLoaded] = useState(false);

  useEffect(() => {
    if (!email) return;

    setSessionsLoaded(false);
    console.log('[Sessions] Fetching sessions from backend for:', email);
    fetchSessions(email)
      .then((data) => {
        const sessionsMap: Record<string, Session> = {};
        (data.data?.sessions || []).forEach((s: any) => {
          // Backend returns sessionId (camelCase)
          const sessionId = s.sessionId || s.session_id || s.id;
          if (!sessionId) return;
          sessionsMap[sessionId] = {
            id: sessionId,
            title: s.title || 'New Chat',
            messages: mapSessionRecordsToMessages(s.messages || []),
            createdAt: s.createdAt || s.created_at || new Date().toISOString(),
          };
        });
        console.log('[Sessions] Loaded from backend:', Object.keys(sessionsMap).length, 'session(s)');
        setSessions(sessionsMap);
        setSessionsLoaded(true);

        const ids = Object.keys(sessionsMap);
        if (ids.length > 0) {
          setActiveSessionId(ids[ids.length - 1]);
        }
      })
      .catch((err) => {
        console.error('[Sessions] Failed to fetch sessions from backend:', err.message);
        setSessionsLoaded(true);
      });
  }, [email]);

  const createSession = useCallback(() => {
    const id = uuidv4();
    const newSession: Session = {
      id,
      title: 'New Chat',
      messages: [],
      createdAt: new Date().toISOString(),
    };

    console.log('[Sessions] Creating new session:', id);

    // Optimistically update UI
    setSessions((prev) => ({ ...prev, [id]: newSession }));
    setActiveSessionId(id);

    // Persist to backend
    if (email) {
      createSessionAPI(email, id).catch((err) =>
        console.error('[Sessions] Failed to create session on backend:', err.message)
      );
    }

    return id;
  }, [email]);

  const addMessage = useCallback((sessionId: string, message: Message) => {
    console.log(`[Sessions] Adding ${message.role} message to session ${sessionId}:`, message.content.slice(0, 60));

    setSessions((prev) => {
      const session = prev[sessionId];
      if (!session) {
        console.warn('[Sessions] Session not found:', sessionId);
        return prev;
      }

      const updatedMessages = [...session.messages, message];

      const title =
        session.title === 'New Chat' && message.role === 'user'
          ? message.content.slice(0, 40) + (message.content.length > 40 ? '...' : '')
          : session.title;

      if (title !== session.title) {
        console.log(`[Sessions] Session title updated to: "${title}"`);
      }

      return {
        ...prev,
        [sessionId]: { ...session, messages: updatedMessages, title },
      };
    });

  }, []);

  const selectSession = useCallback((sessionId: string) => {
    setActiveSessionId(sessionId);

    setSessions((prev) => {
      const session = prev[sessionId];
      if (!session || session.messages.length > 0) return prev;

      fetchSessionById(email as string, sessionId)
        .then((data) => {
          const fullSession = data?.data?.session;
          if (!fullSession) return;
          const messages = mapSessionRecordsToMessages(fullSession.messages || []);
          console.log(`[Sessions] Loaded ${messages.length} message(s) for session ${sessionId}`);
          setSessions((current) => ({
            ...current,
            [sessionId]: {
              ...current[sessionId],
              messages,
            },
          }));
        })
        .catch((err) => {
          console.error('[Sessions] Failed to load session messages:', err.message);
        });

      return prev;
    });
  }, [email]);

  const getActiveSession = useCallback(() => {
    return activeSessionId ? sessions[activeSessionId] : null;
  }, [sessions, activeSessionId]);

  const deleteSession = useCallback((sessionId: string) => {
    console.log('[Sessions] Deleting session:', sessionId);

    setSessions((prev) => {
      const updated = { ...prev };
      delete updated[sessionId];
      return updated;
    });

    setActiveSessionId((prev) => {
      if (prev !== sessionId) return prev;
      const remaining = Object.keys(sessions).filter((id) => id !== sessionId);
      const fallback = remaining.length > 0 ? remaining[remaining.length - 1] : null;
      console.log('[Sessions] Active session deleted. Switching to:', fallback);
      return fallback;
    });

    if (email) {
      deleteSessionAPI(email, sessionId).catch((err) =>
        console.error('[Sessions] Failed to delete session on backend:', err.message)
      );
    }
  }, [email, sessions]);

  return {
    sessions,
    activeSessionId,
    sessionsLoaded,
    setActiveSessionId,
    selectSession,
    createSession,
    addMessage,
    getActiveSession,
    deleteSession,
  };
}
